import * as z from 'zod'
import { langFallback$Fetch } from '~/lang/utils/langfetch'
import { keysToCamel } from '~/utility/toCamel'
import { getURLSearchParams } from '~/utility/url-params'
import { safeJSONParse } from '../../../utility/json'
import { logger } from '../../../utility/logger'
import { BookingSubmitRequest, bookingSubmitRequestZ } from './schema'
import { BookingSubmitService } from './services'

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access --
   TODO: Properly type API response before processing */

const getArgsTransformer = bookingSubmitRequestZ.transform((v) => ({
  adults: v.stayDetails.party.adults,
  arrive: v.stayDetails.range.start,
  depart: v.stayDetails.range.end,
  dogs: v.stayDetails.party.dogs,
  child_ages: v.stayDetails.party.childAges.join(','),
  pitchtype: v.stayDetails.pitchtypeId,
  chargetype: v.stayDetails.chargeTypeId,
}))

const requestTransformer = bookingSubmitRequestZ.transform((v) => ({
  address: v.contactDetails.address.street,
  car_registration_number: v.otherDetails.carRegistration || '',
  city: v.contactDetails.address.city,
  country: v.contactDetails.address.country,
  county: '',
  email: v.contactDetails.email,
  estimated_time_of_arrival: v.otherDetails.estimatedTimeOfArrival || '',
  first_name: v.contactDetails.firstName,
  group_name: v.otherDetails.groupName || '',
  last_name: v.contactDetails.lastName,
  names_of_all_party_members: v.otherDetails.partyNames || '',
  password: v.contactDetails.password || '',
  payment_method_id:
    'paymentMethodId' in v.paymentDetails
      ? v.paymentDetails.paymentMethodId
      : '',
  // Stripe Radar Session ID for improved fraud detection
  radar_session_id: (v.paymentDetails as any).radarSessionId || '',
  // payment_method: {card: 'stored-card'}[v.paymentDetails.method] || v.paymentDetails.method,
  payment_brand: 'bank' in v.paymentDetails ? v.paymentDetails.bank : '',
  payment_selector:
    'cardId' in v.paymentDetails
      ? v.paymentDetails.cardId
      : v.paymentDetails.method,
  postcode: v.contactDetails.address.postCode,
  no_postcode: v.contactDetails.address.noPostCode,
  selected_extras: JSON.stringify(
    Object.keys(v.selectedExtras)
      .map((extra) => ({
        quantity: v.selectedExtras[extra],
        extra,
      }))
      .sort(),
  ),
  should_save_card: 'saveCard' in v.paymentDetails && v.paymentDetails.saveCard,
  special_requests: v.otherDetails.specialRequests || '',
  telephone_0: v.contactDetails.phone.countryDialingCode,
  telephone_1: v.contactDetails.phone.number,
  text_message: v.contactDetails.sendTextMessage,
  unit_type: v.otherDetails.unitType || '',
  dimensions_width: v.otherDetails.unitWidth || '',
  dimensions_depth: v.otherDetails.unitDepth || '',
  take_payment_immediately: v.paymentDetails.takePaymentImmediately,
  // For Stripe connected accounts, pass the account ID to use for payment processing
  stripe_account: v.paymentDetails.stripeAccount || '',
  booking_meta: v.bookingMeta || '',
}))

const locationToResponse = z.object({ location: z.string() }).transform((v) => {
  const location = v.location
  const parts = location.split('/')
  const l = parts.length
  const bookingId = parts[l - 1]
  const taskId = parts[l - 2]
  return {
    bookingId,
    taskId,
  }
})

export class BookingSubmitServiceFetch extends BookingSubmitService {
  async submit(request: BookingSubmitRequest) {
    logger('BookingSubmitServiceFetch.submit', request)

    let url = `/campsite/${request.slug}/book/?${getURLSearchParams(
      getArgsTransformer.parse(request),
    ).toString()}`
    if (request.paymentDetails.method === 'paypal') {
      url += '&new_paypal_checkout=1'
    }
    logger('booking submit url', url, request)
    const response = await langFallback$Fetch(request.langCode, url, {
      method: 'POST',
      credentials: 'same-origin',
      body: getURLSearchParams(requestTransformer.parse(request)).toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'X-ELDARION-AJAX': 'true',
      },
    })
    const results = keysToCamel(response)
    logger('booking submit response', results)
    if (results.error && results.errorCode) {
      // eslint-disable-next-line no-throw-literal -- XState machine expects specific error shapes for error discrimination
      throw {
        alreadyBooked: new Error(results.error),
      }
    }
    if (results.errors) {
      const errors: string[] = Object.values(results.errors)
      const mappedErrors = errors.map((e) => safeJSONParse(e))
      // eslint-disable-next-line no-throw-literal -- XState machine expects specific error shapes for error discrimination
      throw {
        formErrors: Object.assign({}, ...mappedErrors),
      }
    }
    if (!results.location) {
      // eslint-disable-next-line no-throw-literal -- XState machine expects specific error shapes for error discrimination
      throw {
        unknownError: new Error(JSON.stringify({ response, results })),
      }
    }

    return locationToResponse.parse(results)
  }
}
